import Axios from "axios"

import { getSpearlyToken } from "lib/spearlyToken"

// ログイン中のユーザーのパスワード（auth-api #1224 / P-4 の `POST /profile/password`）。
// **この画面の API 呼び出しはこのファイルだけに置く**（#1224 はレビュー中で、形が変わりうる）。
//
// lib/api.ts を通さないのは passwordAuth.ts と同じ理由: 422 の本文
// （`{ errors: { current_password: "is invalid" } }` など）を画面が見分ける必要があり、
// lib/api.ts はそれを捨ててしまう。結果は例外ではなく値で返す。
export type ProfilePasswordResult<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; status: number | null; errors: Record<string, string> }

const request = async <T>(
  method: "get" | "post",
  path: string,
  data?: Record<string, unknown>,
): Promise<ProfilePasswordResult<T>> => {
  const config = {
    headers: {
      Accept: "application/vnd.spearly.v2+json",
      Authorization: `Bearer ${getSpearlyToken()}`,
    },
  }
  try {
    const res =
      method === "get"
        ? await Axios.get<T>(`${process.env.REACT_APP_API_URL}/${path}`, config)
        : await Axios.post<T>(
            `${process.env.REACT_APP_API_URL}/${path}`,
            data,
            config,
          )
    return { ok: true, data: res.data }
  } catch (err) {
    if (Axios.isAxiosError(err) && err.response) {
      const body = err.response.data as { errors?: Record<string, string> }
      return {
        ok: false,
        status: err.response.status,
        errors: body && body.errors ? body.errors : {},
      }
    }
    return { ok: false, status: null, errors: {} }
  }
}

type ProfileResponse = { data: { attributes: { has_password?: boolean } } }

// パスワードが設定済みか（`GET /profile` の `has_password`。#1170）
export const fetchHasPassword = async (): Promise<
  ProfilePasswordResult<boolean>
> => {
  const result = await request<ProfileResponse>("get", "profile")
  if (!result.ok) return result
  return { ok: true, data: result.data.data.attributes.has_password === true }
}

type SetPasswordPayload = {
  currentPassword?: string
  password: string
  passwordConfirmation: string
}

// 設定済みなら currentPassword が必須（auth-api 側で 422 current_password: is invalid）
export const postProfilePassword = (payload: SetPasswordPayload) =>
  request("post", "profile/password", {
    ...(payload.currentPassword !== undefined
      ? { current_password: payload.currentPassword }
      : {}),
    password: payload.password,
    password_confirmation: payload.passwordConfirmation,
  })
